import { useState, useEffect, useRef } from 'react';
import Sidebar from './components/Sidebar';
import ControlsPanel from './components/ControlsPanel';
import PreviewPanel from './components/PreviewPanel';
import CodePanel from './components/CodePanel';
import MobileGate from './components/MobileGate';
import ChartSwitchDialog from './components/ChartSwitchDialog';
import HomeState from './components/HomeState';
import { CHART_TYPES } from './charts';
import { useData } from './contexts/DataContext';
import { useTheme } from './contexts/ThemeContext';
import { resolveStyle } from './charts/shared/themes';
import { computeSmartGuess } from './components/ColumnMappingPanel';
import { applyDeploymentChartConfig } from './config/deploymentProfile';
import { deriveThemeFromBackground, deriveUiFromBackground } from './utils/accessibility';
import { inferTableStructure } from './utils/tableInference';
import { useHistory } from './hooks/useHistory';
import { MOBILE_BREAKPOINT, AUTOSAVE_DEBOUNCE_MS } from './config/constants';

function App() {
  const [selectedChartId, setSelectedChartId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [pendingChartId, setPendingChartId] = useState(null); // chart waiting for dialog confirmation
  const [isRecovering, setIsRecovering] = useState(true);
  const hasRecoveredRef = useRef(false);
  const configCacheRef = useRef({});

  // Use history hook for config state
  const {
    state: config,
    set: setConfig,
    undo: undoConfig,
    redo: redoConfig,
    canUndo: canUndoConfig,
    canRedo: canRedoConfig,
  } = useHistory({});

  // Check for mobile/small screens
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get selected chart type object
  const selectedChart = CHART_TYPES.find(c => c.id === selectedChartId);
  const pendingChart = CHART_TYPES.find(c => c.id === pendingChartId);

  // Update config when chart selection changes (restore from cache if available)
  useEffect(() => {
    if (selectedChart) {
      const cached = configCacheRef.current[selectedChartId];
      if (cached) {
        setConfig(cached);
      } else {
        setConfig((prev) => applyDeploymentChartConfig(selectedChart.defaultConfig, prev));
      }
    }
  }, [selectedChartId, selectedChart, setConfig]);

  // Auto-remap columns when chart type changes with data loaded
  const { isLoaded, rawData, columns, mapping, fileName, setMapping, switchChart, clearData, clearChartData, hasDataForChart, dispatch } = useData();
  // Resolve theme-aware style tokens for code generation
  const { resolved: themeResolved, setBulkOverrides, allThemes, themeId } = useTheme();
  const style = resolveStyle(themeResolved);
  
  // Session Recovery
  useEffect(() => {
    if (hasRecoveredRef.current) return;
    const loadSession = async () => {
      hasRecoveredRef.current = true;
      try {
        const { db } = await import('./utils/db');
        const [appSession, dataSession] = await Promise.all([
          db.get('app_session'),
          db.get('data_session'),
        ]);
        if (dataSession) {
          dispatch({ type: 'RESTORE_SESSION', payload: dataSession });
        }
        if (appSession) {
          if (appSession.configCache) {
            configCacheRef.current = appSession.configCache;
          }
          if (appSession.selectedChartId) {
            if (appSession.config) {
              configCacheRef.current[appSession.selectedChartId] = appSession.config;
            }
            setSelectedChartId(appSession.selectedChartId);
          }
          if (appSession.config) {
            setConfig(appSession.config);
            // Re-apply background-derived theme overrides on restore
            if (appSession.config.backgroundColor && appSession.config.backgroundColor !== '#FFFFFF') {
              const baseTheme = allThemes[themeId] || themeResolved;
              const chartOverrides = deriveThemeFromBackground(appSession.config.backgroundColor, baseTheme);
              const uiOverrides = deriveUiFromBackground(appSession.config.backgroundColor);
              setBulkOverrides({ ...chartOverrides, ui: uiOverrides });
            }
          }
        }
      } catch (err) {
        console.error('Failed to load session:', err);
      } finally {
        setIsRecovering(false);
      }
    };
    loadSession();
  }, [dispatch, setConfig, allThemes, themeId, themeResolved, setBulkOverrides]);

  // Auto-save Session (app state only — data state saved by DataContext)
  useEffect(() => {
    const saveSession = async () => {
      try {
        const { db } = await import('./utils/db');
        await db.set('app_session', { selectedChartId, config, configCache: configCacheRef.current });
      } catch (err) {
        console.error('Failed to save session:', err);
      }
    };
    const timeoutId = setTimeout(saveSession, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [selectedChartId, config]);

  useEffect(() => {
    if (isLoaded && columns.length > 0 && selectedChartId) {
      if (mapping && Object.keys(mapping).length > 0) return;
      const inferred = inferTableStructure(rawData, columns, selectedChartId);
      const newMapping = inferred.suggestedMapping || computeSmartGuess(columns, selectedChartId, rawData);
      setMapping(newMapping);
    }
  }, [selectedChartId, isLoaded, columns, rawData, setMapping, mapping]);

  const prevMappingRef = useRef(null);
  useEffect(() => {
    if (!isLoaded || !mapping || Object.keys(mapping).length === 0) return;
    const mappingKey = JSON.stringify(mapping);
    if (prevMappingRef.current === mappingKey) return;
    prevMappingRef.current = mappingKey;
    setConfig((prev) => {
      const updates = {};
      const isHorizontal = ['horizontal-bar', 'horizontal-grouped-bar', 'horizontal-stacked-bar', 'lollipop', 'diverging-bar'].includes(selectedChartId);
      const isHistogram = selectedChartId === 'histogram';
      const isCombo = selectedChartId === 'combo';

      if (isCombo) {
        if (mapping.category) updates.xlabel = mapping.category;
        if (mapping.barValue) updates.leftLabel = mapping.barValue;
        if (mapping.lineValue) updates.rightLabel = mapping.lineValue;
      } else if (isHistogram) {
        // Value column is on x-axis; y-axis is always 'Frequency' (left as defaultConfig)
        if (mapping.value) updates.xlabel = mapping.value;
      } else if (isHorizontal) {
        // Category on left (ylabel), value on bottom (xlabel)
        if (mapping.value) updates.xlabel = mapping.value;
        if (mapping.category) updates.ylabel = mapping.category;
      } else {
        if (mapping.x) updates.xlabel = mapping.x;
        if (mapping.y) {
          updates.ylabel = mapping.y;
        } else if (mapping.inputShape === 'wide') {
          updates.ylabel = '';
        }
        if (mapping.category && !mapping.x) updates.xlabel = mapping.category;
        if (mapping.value) updates.ylabel = mapping.value;
      }

      if (Object.keys(updates).length === 0) return prev;
      return { ...prev, ...updates };
    });
  }, [mapping, isLoaded, setConfig, selectedChartId]);

  const doSwitchChart = (chartId, keepData) => {
    if (selectedChartId) {
      configCacheRef.current[selectedChartId] = config;
    }
    prevMappingRef.current = null;
    switchChart(chartId, keepData);
    setSelectedChartId(chartId);
  };

  const handleChartSelect = (chartId) => {
    if (chartId === selectedChartId) return;

    // If user has data loaded and target chart has no cached data, confirm
    if (isLoaded && !hasDataForChart(chartId)) {
      setPendingChartId(chartId);
      return;
    }

    // Otherwise switch directly (will restore cached data if any)
    doSwitchChart(chartId, false);
  };

  const handleDialogKeepData = () => {
    doSwitchChart(pendingChartId, true);
    setPendingChartId(null);
  };

  const handleDialogUseSampleData = () => {
    clearChartData(pendingChartId);
    doSwitchChart(pendingChartId, false);
    setPendingChartId(null);
  };

  const handleDialogCancel = () => {
    setPendingChartId(null);
  };

  const handleGoHome = () => {
    if (selectedChartId) {
      configCacheRef.current[selectedChartId] = config;
    }
    setSelectedChartId(null);
    setConfig({});
  };

  const handleResetChart = () => {
    if (!selectedChartId) return;
    delete configCacheRef.current[selectedChartId];
    clearData();
    const defaultCfg = selectedChart?.defaultConfig ?? {};
    setConfig(applyDeploymentChartConfig(defaultCfg, {}));
    prevMappingRef.current = null;
  };

  const handleResetStyles = () => {
    if (!selectedChartId) return;
    delete configCacheRef.current[selectedChartId];
    const defaultCfg = selectedChart?.defaultConfig ?? {};
    setConfig(applyDeploymentChartConfig(defaultCfg, {}));
    prevMappingRef.current = null;
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Propagate UI theme colors as CSS custom properties on <body>
  useEffect(() => {
    const ui = themeResolved.ui;
    if (!ui) return;
    const s = document.body.style;
    s.setProperty('--ui-bg', ui.bg);
    s.setProperty('--ui-bg2', ui.bgSecondary);
    s.setProperty('--ui-text', ui.text);
    s.setProperty('--ui-text2', ui.textSecondary);
    s.setProperty('--ui-border', ui.border);
    s.setProperty('--ui-border2', ui.borderSecondary);
    s.setProperty('--ui-hover', ui.hover);
    s.setProperty('--ui-accent', ui.accent);
    s.setProperty('--ui-accent-fg', ui.accentFg);
    s.setProperty('--ui-scroll-track', ui.scrollTrack);
    s.setProperty('--ui-scroll-thumb', ui.scrollThumb);
    s.setProperty('--ui-overlay-backdrop', ui.overlayBackdrop);
    s.setProperty('--ui-empty-state-text', ui.emptyStateText);
    s.setProperty('--ui-code-keyword', ui.codeKeyword);
    s.setProperty('--ui-code-string', ui.codeString);
    s.setProperty('--ui-code-comment', ui.codeComment);
    s.setProperty('--ui-code-number', ui.codeNumber);
    s.background = ui.bg;
    s.color = ui.text;
  }, [themeResolved]);

  // Generate code (handles both matplotlib and seaborn based on config.outputFormat)
  const dataContext = isLoaded ? { isLoaded, fileName, mapping } : null;
  const generatedCode = selectedChart ? selectedChart.generateCode(config, dataContext, style) : '';

  // Check if chart supports seaborn (has outputFormat in defaultConfig)
  const supportsSeaborn = selectedChart?.defaultConfig?.outputFormat !== undefined;

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        if (e.shiftKey) {
          if (canRedoConfig) redoConfig();
        } else {
          if (canUndoConfig) undoConfig();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndoConfig, canRedoConfig, undoConfig, redoConfig]);

  // Show mobile gate for small screens
  if (isMobile) {
    return <MobileGate />;
  }

  return (
    <div className="h-screen flex overflow-hidden ui-bg">
      {/* Left sidebar - Chart type navigation */}
      <Sidebar
        chartTypes={CHART_TYPES}
        selectedChartId={selectedChartId}
        onChartSelect={handleChartSelect}
        onGoHome={handleGoHome}
      />

      {/* Middle column - Controls (only show when chart selected) */}
      {selectedChart && (
        <ControlsPanel
          chartType={selectedChart}
          config={config}
          onChange={handleConfigChange}
        />
      )}

      {/* Right column - Preview and Code */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {selectedChart ? (
          <>
            <PreviewPanel
              chartType={selectedChart}
              config={config}
              onUndo={undoConfig}
              onRedo={redoConfig}
              canUndo={canUndoConfig}
              canRedo={canRedoConfig}
              onReset={handleResetChart}
              onResetStyles={handleResetStyles}
            />
            <CodePanel
              code={generatedCode}
              supportsSeaborn={supportsSeaborn}
              outputFormat={config.outputFormat}
              onOutputFormatChange={(value) => handleConfigChange('outputFormat', value)}
            />
          </>
        ) : isRecovering ? (
          <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: themeResolved.ui?.bg }} />
        ) : (
          <HomeState theme={themeResolved} />
        )}
      </div>

      {/* Chart switch confirmation dialog */}
      {pendingChartId && pendingChart && (
        <ChartSwitchDialog
          targetChartName={pendingChart.name}
          onKeepData={handleDialogKeepData}
          onUseSampleData={handleDialogUseSampleData}
          onCancel={handleDialogCancel}
        />
      )}
    </div>
  );
}

export default App;
